/**
 * Token Usage Analytics Hooks
 *
 * Hooks para el panel de admin que monitorizan uso de tokens por feature
 * y costes (videos IA: 0.04€/video).
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface TokenUsageSummaryItem {
    feature_key: string;
    feature_label: string;
    total_uses: number;
    total_tokens_consumed: number;
}

export interface TimeSeriesPoint {
    date: string;
    value: number;
}

export interface AiVideoCostSummary {
    total_videos: number;
    cost_eur: number;
    cost_formatted: string;
}

export type TokenFeatureKey = 'ai_video_generation' | 'ai_agent' | 'promote_to_feed';

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useTokenUsageSummary(
    days: number | null = 30,
    startDate?: Date | null,
    endDate?: Date | null
) {
    return useQuery<TokenUsageSummaryItem[]>({
        queryKey: ['admin', 'token-usage-summary', days, startDate?.toISOString(), endDate?.toISOString()],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_token_usage_summary', {
                p_days: days,
                p_start_date: startDate?.toISOString().split('T')[0] || null,
                p_end_date: endDate?.toISOString().split('T')[0] || null,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as TokenUsageSummaryItem[];
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function useTokenUsageTimeseries(
    featureKey: TokenFeatureKey | null,
    days: number | null = 30,
    startDate?: Date | null,
    endDate?: Date | null
) {
    return useQuery<TimeSeriesPoint[]>({
        queryKey: ['admin', 'token-usage-timeseries', featureKey, days, startDate?.toISOString(), endDate?.toISOString()],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_token_usage_timeseries', {
                p_feature_key: featureKey,
                p_days: days,
                p_start_date: startDate?.toISOString().split('T')[0] || null,
                p_end_date: endDate?.toISOString().split('T')[0] || null,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as TimeSeriesPoint[];
        },
        enabled: !!featureKey,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function useAiVideoCostSummary(
    days: number | null = 30,
    startDate?: Date | null,
    endDate?: Date | null
) {
    return useQuery<AiVideoCostSummary>({
        queryKey: ['admin', 'ai-video-cost-summary', days, startDate?.toISOString(), endDate?.toISOString()],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_ai_video_cost_summary', {
                p_days: days,
                p_start_date: startDate?.toISOString().split('T')[0] || null,
                p_end_date: endDate?.toISOString().split('T')[0] || null,
            });
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            return (Array.isArray(normalized) ? normalized[0] : normalized) as AiVideoCostSummary;
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function useAiVideoTimeseries(
    days: number | null = 30,
    startDate?: Date | null,
    endDate?: Date | null
) {
    return useQuery<TimeSeriesPoint[]>({
        queryKey: ['admin', 'ai-video-timeseries', days, startDate?.toISOString(), endDate?.toISOString()],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_ai_video_timeseries', {
                p_days: days,
                p_start_date: startDate?.toISOString().split('T')[0] || null,
                p_end_date: endDate?.toISOString().split('T')[0] || null,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as TimeSeriesPoint[];
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}
